import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const FISCAL_DEADLINES = [
  { date: "03-16", label: "Versamento IVA mensile (16 marzo)" },
  { date: "04-30", label: "Approvazione bilancio (30 aprile)" },
  { date: "05-31", label: "Modello Intrastat (31 maggio)" },
  { date: "06-30", label: "Dichiarazione redditi & IRAP saldo (30 giugno)" },
  { date: "07-31", label: "Mod. 770 e Certificazione Unica" },
  { date: "09-30", label: "Modello IVA TR / rimborsi (30 settembre)" },
  { date: "11-30", label: "Acconto imposte di novembre (30 novembre)" },
  { date: "12-27", label: "Acconto IVA (27 dicembre)" },
];

export default function CalendarPage() {
  const [controls, setControls] = useState([]);
  const [tests, setTests] = useState([]);
  const [actions, setActions] = useState([]);

  useEffect(() => {
    api.get("/controls").then(({ data }) => setControls(data));
    api.get("/control-tests").then(({ data }) => setTests(data));
    api.get("/corrective-actions").then(({ data }) => setActions(data));
  }, []);

  const year = new Date().getFullYear();
  const events = useMemo(() => {
    const ev = [];
    controls.forEach((c) => c.next_due_at && ev.push({ date: c.next_due_at.slice(0, 10), label: c.description, type: "ctrl", color: "bg-slate-800 text-white" }));
    tests.forEach((t) => t.next_due_at && ev.push({ date: t.next_due_at.slice(0, 10), label: `Test ${t.test_type}`, type: "test", color: "bg-green-700 text-white" }));
    actions.forEach((a) => a.due_date && ev.push({ date: a.due_date.slice(0, 10), label: a.title, type: "action", color: "bg-orange-500 text-white" }));
    FISCAL_DEADLINES.forEach((d) => ev.push({ date: `${year}-${d.date}`, label: d.label, type: "fiscale", color: "bg-slate-300 text-slate-900" }));
    return ev.sort((a, b) => a.date.localeCompare(b.date));
  }, [controls, tests, actions, year]);

  // Build a simple month grid for current month
  const [cursor, setCursor] = useState(new Date());
  const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
  const startDow = (monthStart.getDay() + 6) % 7; // mon-first
  const days = [];
  for (let i = 0; i < startDow; i++) days.push(null);
  for (let d = 1; d <= monthEnd.getDate(); d++) days.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));

  const fmt = (d) => d.toISOString().slice(0, 10);
  const monthName = cursor.toLocaleString("it-IT", { month: "long", year: "numeric" });

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div>
        <div className="label-tag">Scadenze</div>
        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900 mt-1">Calendario</h1>
      </div>

      <div className="grid grid-cols-12 gap-6">
        <Card className="col-span-9 card-flat p-6">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="text-sm text-slate-600 hover:text-slate-900" data-testid="cal-prev">← Mese prec.</button>
            <h3 className="font-display text-xl font-semibold capitalize" data-testid="cal-month-label">{monthName}</h3>
            <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="text-sm text-slate-600 hover:text-slate-900" data-testid="cal-next">Mese succ. →</button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-xs text-slate-500 uppercase tracking-wider mb-2">
            {["lun", "mar", "mer", "gio", "ven", "sab", "dom"].map((d) => <div key={d} className="text-center font-semibold">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {days.map((d, i) => {
              if (!d) return <div key={i} className="aspect-square bg-slate-50 border border-slate-100" />;
              const key = fmt(d);
              const dayEvents = events.filter((e) => e.date === key).slice(0, 3);
              return (
                <div key={i} className="aspect-square border border-slate-200 p-1 text-xs flex flex-col" data-testid={`day-${key}`}>
                  <div className="text-slate-600 data-num">{d.getDate()}</div>
                  <div className="flex-1 mt-1 space-y-0.5">
                    {dayEvents.map((e, j) => (
                      <div key={j} className={`${e.color} text-[10px] px-1 py-0.5 rounded-sm truncate`}>{e.label}</div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="col-span-3 card-flat p-5">
          <div className="label-tag mb-3">Prossime 14 scadenze</div>
          <div className="space-y-2 max-h-[560px] overflow-y-auto">
            {events.slice(0, 14).map((e, i) => (
              <div key={i} className="border-b border-slate-100 py-2 text-xs">
                <div className="data-num text-slate-500">{e.date}</div>
                <div className="text-slate-800 mt-0.5">{e.label}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
